import { LoaderCircle } from "lucide-react";
import type { Dokument, Frist } from "../api/typen";
import { betrag, betragGesprochen, datumGesprochen, datumsZeile, fristText } from "../format";
import { zeilenAbsender, zeilenDatum } from "../seiten/vorschlag";
import { Chip, FristBadge } from "./Badge";
import { Zeile } from "./Gruppe";
import { Kachel } from "./Kachel";
import { PersonMarke } from "./PersonMarke";
import { heuteBerlin } from "../welt/lage";
import { fristRechts } from "../welt/saetze";
import { Zustand } from "../welt/Zustand";

export type Namen = {
  absender: (id: string | null) => string | undefined;
  person: (id: string | null) => string | undefined;
  kategorie: (id: string | null) => { name: string; schluessel: string | null } | undefined;
  /** Farbe der Personen-Marke; undefined, solange der Haushalt nur eine Person hat (Plan 6.6). */
  farbe?: (id: string | null, gemeinsam?: boolean) => string | undefined;
};

const WARTET: Partial<Record<Dokument["status"], string>> = {
  eingang: "Wartet auf Erkennung",
  verarbeitung: "Wird erkannt …",
  fehler: "Konnte nicht verarbeitet werden",
  pruefen: "Bitte prüfen",
};

/** Dokumentzeile (Handoff PgInbox, Listenzeile): neutrale Kachel, Absender fett mit Betrag rechts,
 *  Betreff und Datum mit Frist-Chip, Personen-Marke nur bei mehreren Personen. Die ganze Zeile fuehrt
 *  zum Dokument. W37e (Sichtpruefung 08.10.): solange der Brief nicht abgelegt ist, zeigt sie wie der
 *  Dokumentkopf den Absender aus dem Vorschlag mit Kennzeichen „Vorschlag“ und „Brief vom …“; abgelegt gilt
 *  nur Bestaetigtes. W37g-Web: beides kommt aus der Kurzform `vorschlag_kurz` des Listeneintrags (Server W37f,
 *  zeilenAbsender, zeilenDatum); die Zeile stellt keine eigene Abfrage. Fehlt die Kurzform (aelterer Server),
 *  zeigt sie keinen Vorschlag. */
/** W82-2 N3 (FW9, Vorlage R11 N1 „Neue Post“): mit `post` traegt die Zeile die Gestalt der neuen Post auf der Startseite:
 *  Linse mit Zustandspunkt (Ring bei offener Frist, Raute ueberfaellig, Punkt bald, Mint ohne offene Frist), Absender bis
 *  zwei Zeilen statt gekuerzt, „€“ klein. W82-2 N4 (D-453, R11 `.post .zeile`): der Zweck in 14 ungekuerzt unter Absender
 *  und Betrag, die Frist als dritte Zeile mit Form und Fristwort wie iOS (FW12, `fristRechts`: „bis 05.11“, „seit 3 Tagen“,
 *  „in 5 Tagen“), ohne Kasten; der Lesetext nennt die Frist mit demselben Wort. Kennungen und Ziel bleiben (welt.css). */
export function DokumentZeile(props: { dokument: Dokument; namen: Namen; frist?: Frist; post?: boolean }) {
  const d = props.dokument;
  const absender = props.namen.absender(d.absender_id);
  // gemeinsame Post traegt keine einzelne Person, aber ein eigenes Etikett (D-051)
  const person = d.gemeinsam ? "Gemeinsam" : props.namen.person(d.person_id);
  const kategorie = props.namen.kategorie(d.kategorie_id);
  const farbe = props.namen.farbe?.(d.person_id, d.gemeinsam);
  const inArbeit = d.status === "eingang" || d.status === "verarbeitung";
  // W37g-Web: nur aus dem Listeneintrag, kein Detail-Abruf je Zeile
  const vorgeschlagen = zeilenAbsender(d, absender);
  const titel = absender ?? vorgeschlagen ?? (inArbeit ? "Neuer Brief" : d.betreff ?? "Ohne Absender");
  const zweite = absender ? d.betreff : WARTET[d.status];
  const mitDatum = { ...d, datum: zeilenDatum(d) };
  // FW9, FW12: Zustand und Fristwort der neuen Post wie iOS (`fristRechts`, Europe/Berlin)
  const postFrist = props.post && props.frist ? fristRechts(props.frist, heuteBerlin(), betrag) : undefined;
  const punkt = !postFrist || postFrist.zustand === "erledigt" ? "gut" : postFrist.zustand;

  const gesprochen = [
    titel,
    vorgeschlagen ? "Vorschlag" : undefined,
    zweite,
    datumGesprochen(mitDatum),
    kategorie?.name,
    person,
    betragGesprochen(d.betrag),
    props.frist ? `Frist ${postFrist ? postFrist.text : fristText(props.frist.datum, Boolean(props.frist.erledigt)).text}` : undefined,
    WARTET[d.status] && absender ? WARTET[d.status] : undefined,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Zeile
      zu={`/dokument/${d.id}`}
      label={gesprochen}
      mitKachel
      textUnterKachel
      uebergang
      vorne={
        <span className={props.post ? "post-linse" : undefined} style={{ viewTransitionName: `kachel-${d.id}` }}>
          <Kachel schluessel={kategorie?.schluessel} />
          {props.post && <span className="zp" data-zustand={punkt} aria-hidden="true" />}
        </span>
      }
    >
      <span className="zeile-paar flex items-baseline justify-between gap-3">
        {/* W37e: das Kennzeichen rutscht bei wenig Platz unter den Absender, der Betrag rechts bleibt stehen */}
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="t-body t-body-medium max-w-full min-w-0 truncate" style={{ viewTransitionName: `titel-${d.id}` }}>{titel}</span>
          {vorgeschlagen && (
            <span data-testid="zeile.absender.vorschlag" className="inline-flex max-w-full min-w-0">
              <Chip zustand="ki" className="kennzeichen">
                Vorschlag
              </Chip>
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-3">
          {person && farbe && <PersonMarke name={person} farbe={farbe} groesse="klein" />}
          {d.betrag &&
            (props.post ? (
              <span className="t-body t-body-medium t-zahl whitespace-nowrap">
                {betrag(d.betrag).replace(/\s*€$/u, "")}
                <small className="post-einheit">{"\u202f€"}</small>
              </span>
            ) : (
              <span className="t-body t-body-medium t-zahl whitespace-nowrap">{betrag(d.betrag)}</span>
            ))}
        </span>
      </span>
      {/* W64 N1: wird die Fussnote neben dem Frist-Chip zu schmal, rutscht der Chip darunter (bausteine.css); in der neuen Post
          steht die Frist als eigene dritte Zeile, die Fussnote (Zweck) ist ungekuerzt (N4) */}
      <span className="zeile-paar zeile-paar-fussnote flex items-start justify-between gap-3">
        <span className={`zeile-fussnote t-footnote text-pg-text-2 ${props.post ? "post-zweck" : "line-clamp-2"}`}>
          {inArbeit && <LoaderCircle size={16} strokeWidth={1.75} aria-hidden className="mr-1 inline animate-spin align-text-bottom motion-reduce:animate-none" />}
          {zweite && `${zweite} · `}
          {/* W51 (D-073): die Datumszeile als Ganzes, im Datum bricht nichts (bausteine.css, .datum-ganz) */}
          <span className="datum-ganz">{datumsZeile(mitDatum)}</span>
        </span>
        {props.frist && !props.post && <FristBadge datum={props.frist.datum} erledigt={Boolean(props.frist.erledigt)} />}
      </span>
      {/* N4: die Frist als dritte Textzeile der Zeile (wie die beiden darüber ein eigenes `zeile-paar`) */}
      {postFrist && (
        <span className="zeile-paar zeile-paar-frist flex">
          <span className="frist-badge post-frist w-zustand" data-zustand={postFrist.zustand}>
            <Zustand form={postFrist.zustand === "ueberfaellig" ? "raute" : postFrist.zustand === "spaeter" ? "ring" : "punkt"} />
            {postFrist.text}
          </span>
        </span>
      )}
    </Zeile>
  );
}
